import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Card, Badge } from '../ui';
import {
  flushMessageBuffer,
  getOpsMonitorMetrics,
  sendReportDeliveryTest,
  traceMessageFlowByLink,
} from '../../services/metricsService';

const OPS_MONITOR_REFETCH_MS = 180000;
const DEFAULT_BUCKET_MINUTES = 60;
const DEFAULT_HANDLER_BUCKET_MINUTES = 60;
const FLOW_SUMMARY_REASONS = [
  ['invalid_sender', 'Недействительный отправитель'],
  ['non_group_chat', 'Не группа'],
  ['invalid_text', 'Пустой текст'],
  ['invalid_length', 'Длина текста'],
  ['blacklist_match', 'В чёрном списке'],
  ['own_message', 'Собственные'],
  ['flow_debug_message', 'Отладочные'],
];
const FLOW_REASON_DETAILS = {
  invalid_sender: 'sender_id не типа messageSenderUser',
  non_group_chat: 'chat_id не групповой (ожидается chat_id < 0)',
  invalid_text: 'в сообщении нет текстового контента для анализа',
  invalid_length: 'длина текста вне диапазона 1..500 (боевой диапазон 25..500, короткие 1..25 track-only)',
  blacklist_match: 'сообщение содержит слово из blacklist',
  own_message: 'исходящее/собственное сообщение аккаунта',
  flow_debug_message: 'служебное debug/relay сообщение пайплайна',
};
const INVALID_SENDER_TYPE_DETAILS = {
  messageSenderChat: 'Пост от имени чата/канала (анонимный админ, канал в группе).',
  messageSenderUser: 'Обычный пользователь (обычно не должен попадать в invalid_sender).',
  missing_sender: 'В апдейте отсутствует sender_id.',
};
const MATCH_STATUS_META = {
  queued: {
    label: 'В очереди отправки',
    description: 'Совпадение найдено, событие поставлено в очередь report-группы.',
  },
  sent: {
    label: 'Отправлено в report-группу',
    description: 'Очередь отправила сообщение в Telegram успешно.',
  },
  send_failed: {
    label: 'Ошибка отправки',
    description: 'Очередь не смогла отправить сообщение в report-группу.',
  },
  duplicate_message: {
    label: 'Дубликат (не отправляли)',
    description: 'Совпадение найдено, но отправка пропущена дедупликацией.',
  },
  matched_but_not_routed: {
    label: 'Совпало, но маршрут невалиден',
    description: 'Ключи совпали, но нет валидной связки bot_token/report_group_id/клиента.',
  },
  filtered_project_check: {
    label: 'Не прошло проверку проекта',
    description: 'Сообщение дошло до project-check, но отфильтровано (ключи/минус-слова/контент/бот).',
  },
  project_check_error: {
    label: 'Ошибка project-check',
    description: 'На этапе processProjectMessage возникло исключение.',
  },
};
const PROJECT_FLOW_REASON_LABELS = {
  generated_report_message: 'Служебный report-шаблон (защита от зацикливания)',
  no_text_content: 'Нет текстового контента для проверки',
  rejected_minus_word: 'Сработало минус-слово проекта',
  no_primary_keywords_matched: 'Нет совпадений по primary-ключам',
  no_secondary_keywords_matched: 'Нет совпадений по secondary-ключам',
  no_primary_keywords_defined: 'В проекте не заданы primary/legacy ключи',
  invalid_message_text: 'Некорректный текст сообщения',
  bot_sender: 'Отправитель — бот',
  missing_routing_config: 'Не заполнены bot_token и/или report_group_id',
  report_and_source_chat_equal: 'source chat совпал с report group',
  tdlib_client_unavailable: 'Нет живого TDLib-клиента для apiId',
  chat_not_found: 'Чат не найден в кэше/БД',
  duplicate_message: 'Дедупликация очереди',
  send_failed: 'Ошибка отправки в Telegram',
  unknown: 'Неуточнённая причина',
};
const KEYMATCH_POSITIVE_STATUSES = new Set([
  'queued',
  'sent',
  'send_failed',
  'duplicate_message',
  'matched_but_not_routed',
]);

const formatK = (value) => {
  if (!value) return '0';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
};

const formatPct = (value) => `${(value * 100).toFixed(1)}%`;

const buildTelegramChatMessageLink = (chatId, messageId) => {
  if (!chatId || !messageId) {
    return null;
  }

  const chatIdStr = String(chatId);
  if (!chatIdStr.startsWith('-100')) {
    return null;
  }

  const internalChatId = chatIdStr.replace('-100', '');
  if (!internalChatId) {
    return null;
  }

  return `https://t.me/c/${internalChatId}/${messageId}`;
};

const getMatchStatusMeta = (status) => {
  if (status && MATCH_STATUS_META[status]) {
    return MATCH_STATUS_META[status];
  }

  return {
    label: status || 'unknown',
    description: 'Статус без преднастроенного пояснения.',
  };
};

const getMatchStatusBadgeClass = (status) => {
  if (status === 'sent') {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (status === 'send_failed' || status === 'project_check_error') {
    return 'bg-red-100 text-red-700';
  }
  if (status === 'queued' || status === 'filtered_project_check') {
    return 'bg-amber-100 text-amber-700';
  }
  if (status === 'matched_but_not_routed') {
    return 'bg-blue-100 text-blue-700';
  }
  return 'bg-gray-100 text-gray-700';
};

const getProjectFlowReasonLabel = (reason) => {
  if (!reason) {
    return null;
  }
  return PROJECT_FLOW_REASON_LABELS[reason] || reason;
};

const getTraceVerdict = (trace) => {
  const exactStage = trace?.exactStage || null;
  if (exactStage?.key) {
    const toneByKey = {
      sent: 'green',
      queued: 'blue',
      sending: 'blue',
      waiting_in_message_buffer: 'blue',
      duplicate_message: 'amber',
      filtered_on_keyword_check: 'amber',
      db_match_recorded: 'amber',
      missing_routing_config: 'red',
      report_and_source_chat_equal: 'red',
      tdlib_client_unavailable: 'red',
      bot_sender: 'red',
      send_failed: 'red',
      project_check_error: 'red',
    };

    return {
      tone: toneByKey[exactStage.key] || 'amber',
      title: exactStage.projectName
        ? `${exactStage.label} · ${exactStage.projectName}`
        : exactStage.label,
      description: exactStage.reason
        ? `Код/причина: ${exactStage.reason}`
        : `Источник: ${exactStage.source || 'trace'}`,
    };
  }

  const handlerStatus = trace?.stageSummary?.handler?.status || 'unknown';
  const matchedProjects = Number(trace?.stageSummary?.keywordChecks?.matchedProjects || 0);
  const delivery = trace?.stageSummary?.delivery || {};
  const runtimeByStatus = delivery?.runtimeByStatus || {};
  const sent = Number(runtimeByStatus.sent || 0);
  const queued = Number(runtimeByStatus.queued || 0);
  const sendFailed = Number(runtimeByStatus.send_failed || 0);
  const duplicate = Number(runtimeByStatus.duplicate_message || 0);
  const invalidRoute = Number(runtimeByStatus.matched_but_not_routed || 0);

  if (handlerStatus !== 'pass') {
    return {
      tone: 'red',
      title: 'Сообщение отвалилось до этапа project-check',
      description: trace?.stageSummary?.handler?.reasonLabel || 'Сообщение не прошло flow-фильтры.',
    };
  }

  if (matchedProjects <= 0) {
    return {
      tone: 'amber',
      title: 'До project-check дошло, но ключи не собрали матч',
      description: 'Сообщение прошло flow-фильтры, но ни один проект не дал финального совпадения.',
    };
  }

  if (sent > 0) {
    return {
      tone: 'green',
      title: 'Сообщение дошло до report-группы',
      description: `Есть успешная отправка: ${formatK(sent)}.`,
    };
  }

  if (queued > 0) {
    return {
      tone: 'blue',
      title: 'Сообщение совпало и поставлено в очередь',
      description: `Очередь зафиксирована: ${formatK(queued)}, но success/sent ещё не видно.`,
    };
  }

  if (sendFailed > 0) {
    return {
      tone: 'red',
      title: 'Сообщение совпало, но отправка упала',
      description: `Есть ошибка отправки: ${formatK(sendFailed)}.`,
    };
  }

  if (duplicate > 0) {
    return {
      tone: 'amber',
      title: 'Сообщение совпало, но было отброшено как дубликат',
      description: `Очередь пометила duplicate_message: ${formatK(duplicate)}.`,
    };
  }

  if (invalidRoute > 0) {
    return {
      tone: 'amber',
      title: 'Сообщение совпало, но маршрут невалиден',
      description: `Есть matched_but_not_routed: ${formatK(invalidRoute)}.`,
    };
  }

  return {
    tone: 'amber',
    title: 'Совпадение есть, но финальная доставка не подтверждена',
    description: 'Нужны runtime-события queued/sent/failed, чтобы увидеть итог после keyword-match.',
  };
};

const getTraceVerdictClasses = (tone) => {
  if (tone === 'green') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  }
  if (tone === 'red') {
    return 'border-red-200 bg-red-50 text-red-800';
  }
  if (tone === 'blue') {
    return 'border-blue-200 bg-blue-50 text-blue-800';
  }
  return 'border-amber-200 bg-amber-50 text-amber-800';
};

const hasKeywordMatchInEvent = (event) => {
  const keywords = Array.isArray(event?.keywords) ? event.keywords : [];
  if (keywords.length > 0) {
    return true;
  }

  if (KEYMATCH_POSITIVE_STATUSES.has(event?.statusKey)) {
    return true;
  }

  if (event?.statusKey === 'filtered_project_check' && event?.reason === 'bot_sender') {
    return true;
  }

  return false;
};

const summarizeDataSource = (source) => {
  switch (source) {
    case 'runtime':
      return 'runtime';
    case 'metrics_db_window':
      return 'db:Metrics (window)';
    case 'matches_db_window':
      return 'db:matches (window)';
    case 'flow_summary_window':
      return 'runtime flow (window)';
    case 'flow_summary_cumulative':
      return 'runtime flow (cumulative)';
    default:
      return 'не выбран';
  }
};

const MessageVolumeMonitorPanel = () => {
  const [windowHours, setWindowHours] = useState(24);
  const [recentViewMode, setRecentViewMode] = useState('messages');
  const [onlyKeywordPassed, setOnlyKeywordPassed] = useState(true);
  const [recentStatusFilter, setRecentStatusFilter] = useState('all');
  const [recentPageSize, setRecentPageSize] = useState(20);
  const [recentPage, setRecentPage] = useState(1);
  const [traceLinkInput, setTraceLinkInput] = useState('');
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceError, setTraceError] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [traceActionLoading, setTraceActionLoading] = useState(false);
  const [traceActionError, setTraceActionError] = useState(null);
  const [traceActionSuccess, setTraceActionSuccess] = useState(null);
  const [deliveryTestByProject, setDeliveryTestByProject] = useState({});

  const { data, isLoading, error } = useQuery(
    ['dashboard-ops-monitor', windowHours],
    () => getOpsMonitorMetrics({
      windowHours,
      bucketMinutes: DEFAULT_BUCKET_MINUTES,
      flowWindowHours: windowHours,
      flowBucketMinutes: DEFAULT_BUCKET_MINUTES,
      handlerWindowHours: windowHours,
      handlerBucketMinutes: DEFAULT_HANDLER_BUCKET_MINUTES,
    }),
    {
      refetchInterval: OPS_MONITOR_REFETCH_MS,
      keepPreviousData: true,
    }
  );

  const payload = data?.data;
  const flowSummary = payload?.flowSummary || null;
  const projectFlowSummary = payload?.projectFlowSummary?.cumulative || null;
  const summary = useMemo(
    () => payload?.summary || { totalProcessed: 0, totalMatched: 0, matchRate: 0, sources: { totalProcessed: 'none', totalMatched: 'none' } },
    [payload]
  );
  const anomaly = payload?.anomaly || {};
  const handlerFlowSummary = payload?.handlerFlowSummary || null;
  const hourlySummary = useMemo(() => {
    const hourly = payload?.systemVolume?.hourly || [];
    const lastPoint = hourly[hourly.length - 1] || {};
    const total = hourly.reduce((sum, row) => sum + Number(row.processed || 0), 0);
    const matched = hourly.reduce((sum, row) => sum + Number(row.matched || 0), 0);
    const totalHours = Math.max(1, hourly.length);

    return {
      lastProcessed: Number(lastPoint.processed || 0),
      lastMatched: Number(lastPoint.matched || 0),
      avgPerHour: total > 0 ? Math.round(total / totalHours) : 0,
      totalProcessed: total,
      totalMatched: matched,
      windowHours: totalHours,
      matchRate: total > 0 ? matched / total : 0,
    };
  }, [payload]);

  const dailySummary = useMemo(() => {
    const daily = payload?.systemVolume?.daily || [];
    const lastPoint = daily[daily.length - 1] || {};
    const total = daily.reduce((sum, row) => sum + Number(row.processed || 0), 0);
    const matched = daily.reduce((sum, row) => sum + Number(row.matched || 0), 0);
    const totalDays = Math.max(1, daily.length);

    return {
      lastProcessed: Number(lastPoint.processed || 0),
      lastMatched: Number(lastPoint.matched || 0),
      avgPerDay: total > 0 ? Math.round(total / totalDays) : 0,
      totalProcessed: total,
      totalMatched: matched,
      windowDays: totalDays,
      matchRate: total > 0 ? matched / total : 0,
    };
  }, [payload]);

  const displaySummary = useMemo(() => ({
    totalProcessed: Number(summary.totalProcessed || 0),
    totalMatched: Number(summary.totalMatched || 0),
    matchRate: Number(summary.matchRate || 0),
    sourceProcessed: summary.sources?.totalProcessed || 'none',
    sourceMatched: summary.sources?.totalMatched || 'none',
  }), [summary]);

  const processedDrop = anomaly.processedDrop;
  const matchedDrop = anomaly.matchedDrop;
  const diagnosis = payload?.systemVolume?.diagnosis;
  const projectFlow = useMemo(() => ({
    totalChecks: Number(projectFlowSummary?.totalChecks || 0),
    keywordMatched: Number(projectFlowSummary?.keywordMatched || 0),
    routedQueued: Number(projectFlowSummary?.routedQueued || 0),
    routedDuplicate: Number(projectFlowSummary?.routedDuplicate || 0),
    matchedButNotRouted: Number(projectFlowSummary?.matchedButNotRouted || 0),
    deliverySent: Number(projectFlowSummary?.deliverySent || 0),
    deliveryFailed: Number(projectFlowSummary?.deliveryFailed || 0),
    failedByReason: projectFlowSummary?.failedByReason || {},
    recentMatches: Array.isArray(projectFlowSummary?.recentMatches) ? projectFlowSummary.recentMatches : [],
  }), [projectFlowSummary]);

  const hasNoData = !displaySummary.totalProcessed && !displaySummary.totalMatched;
  const flowSummaryWindowTotals = flowSummary?.windowTotals || null;

  const hasWindowFlowStats = Boolean(
    flowSummaryWindowTotals && (
      Number(flowSummaryWindowTotals.total || 0) > 0
      || Number(flowSummaryWindowTotals.accepted || 0) > 0
      || Number(flowSummaryWindowTotals.rejected || 0) > 0
      || Number(flowSummaryWindowTotals.withProjects || 0) > 0
      || Number(flowSummaryWindowTotals.withoutProjects || 0) > 0
      || Number(flowSummaryWindowTotals.markReadFailures || 0) > 0
      || Object.values(flowSummaryWindowTotals?.rejectedByReason || {}).some((value) => Number(value || 0) > 0)
    )
  );

  const flowSummaryTotals = useMemo(() => {
    const source = hasWindowFlowStats ? flowSummaryWindowTotals : flowSummary?.cumulative || null;
    const rejectedByReason = source?.rejectedByReason || {};
    return {
      total: Number(source?.total || 0),
      accepted: Number(source?.accepted || 0),
      rejected: Number(source?.rejected || 0),
      withProjects: Number(source?.withProjects || 0),
      withoutProjects: Number(source?.withoutProjects || 0),
      markReadFailures: Number(source?.markReadFailures || 0),
      rejectedByReason,
    };
  }, [flowSummaryWindowTotals, hasWindowFlowStats, flowSummary]);

  const flowSummaryCumulative = flowSummary?.cumulative;
  const flowSummaryWindowHours = flowSummary?.windowParams?.hours;
  const invalidSenderByType = useMemo(
    () => flowSummaryCumulative?.invalidSenderByType || {},
    [flowSummaryCumulative]
  );
  const flowSummaryReasonLabel = useMemo(() => {
    if (hasWindowFlowStats) {
      return `за последние ${flowSummaryWindowHours || windowHours} ч`;
    }

    return flowSummaryCumulative
      ? `кумулятивно: ${formatK(flowSummaryCumulative.total || 0)}`
      : 'кумулятивно';
  }, [windowHours, hasWindowFlowStats, flowSummaryCumulative, flowSummaryWindowHours]);

  const flowReasonSummary = useMemo(() => {
    const source = hasWindowFlowStats ? flowSummaryWindowTotals : flowSummaryCumulative || null;
    const rejectedByReason = source?.rejectedByReason || {};
    return FLOW_SUMMARY_REASONS
      .map(([reason, label]) => ({
        reason,
        label,
        value: Number(rejectedByReason?.[reason] || 0),
      }))
      .filter((item) => item.value > 0);
  }, [flowSummaryWindowTotals, hasWindowFlowStats, flowSummaryCumulative]);

  const totalRejectedByReason = useMemo(
    () => flowReasonSummary.reduce((sum, item) => sum + item.value, 0),
    [flowReasonSummary]
  );
  const otherRejected = Math.max(0, Number(flowSummaryTotals.rejected || 0) - totalRejectedByReason);

  const detailedReasonRows = useMemo(() => {
    const rows = flowReasonSummary.map((item) => ({
      reason: item.reason,
      label: item.label,
      value: item.value,
      pctOfRejected: flowSummaryTotals.rejected > 0 ? item.value / flowSummaryTotals.rejected : 0,
      pctOfTotal: flowSummaryTotals.total > 0 ? item.value / flowSummaryTotals.total : 0,
      description: FLOW_REASON_DETAILS[item.reason] || '—',
    }));

    if (otherRejected > 0) {
      rows.push({
        reason: 'other',
        label: 'Прочее / неразмеченные причины',
        value: otherRejected,
        pctOfRejected: flowSummaryTotals.rejected > 0 ? otherRejected / flowSummaryTotals.rejected : 0,
        pctOfTotal: flowSummaryTotals.total > 0 ? otherRejected / flowSummaryTotals.total : 0,
        description: 'разница между общим rejected и суммой известных reason-кодов',
      });
    }

    return rows;
  }, [flowReasonSummary, flowSummaryTotals, otherRejected]);

  const hasWindowHandlerFlowStats = Boolean(
    handlerFlowSummary?.timeline?.updates &&
    Array.isArray(handlerFlowSummary.timeline.updates) &&
    handlerFlowSummary.timeline.updates.length > 0 &&
    handlerFlowSummary?.windowTotals &&
    (
      Number(handlerFlowSummary.windowTotals.updates || 0) > 0
      || Number(handlerFlowSummary.windowTotals.buffered || 0) > 0
      || Number(handlerFlowSummary.windowTotals.handlerErrors || 0) > 0
      || Number(handlerFlowSummary.windowTotals.skippedOwnMessages || 0) > 0
      || Number(handlerFlowSummary.windowTotals.skippedInactiveAccount || 0) > 0
    )
  );

  const handlerFlowTotals = useMemo(() => {
    const source = hasWindowHandlerFlowStats
      ? handlerFlowSummary?.windowTotals
      : handlerFlowSummary?.cumulative || null;

    return {
      updates: Number(source?.updates || 0),
      nonMessageUpdates: Number(source?.nonMessageUpdates || 0),
      skippedMissingClient: Number(source?.skippedMissingClient || 0),
      skippedInactiveAccount: Number(source?.skippedInactiveAccount || 0),
      skippedOwnMessages: Number(source?.skippedOwnMessages || 0),
      buffered: Number(source?.buffered || 0),
      handlerErrors: Number(source?.handlerErrors || 0),
    };
  }, [hasWindowHandlerFlowStats, handlerFlowSummary]);

  const reportForwardedEstimate = Number(projectFlow.routedQueued || 0);
  const reportForwardedSourceLabel = 'runtime:project_flow';
  const notReachedReportAfterProjectStage = Math.max(
    0,
    Number(flowSummaryTotals.withProjects || 0) - reportForwardedEstimate
  );

  const flowFunnelRows = useMemo(() => {
    const updates = Number(handlerFlowTotals.updates || 0);
    const buffered = Number(handlerFlowTotals.buffered || 0);
    const total = Number(flowSummaryTotals.total || 0);
    const accepted = Number(flowSummaryTotals.accepted || 0);
    const withProjects = Number(flowSummaryTotals.withProjects || 0);
    const withoutProjects = Number(flowSummaryTotals.withoutProjects || 0);
    const rejected = Number(flowSummaryTotals.rejected || 0);
    const forwarded = reportForwardedEstimate;

    return [
      {
        label: 'Апдейты от Telegram (handler updates)',
        value: updates,
        base: updates,
      },
      {
        label: 'Буферизовано как сообщения',
        value: buffered,
        base: updates || buffered,
      },
      {
        label: 'Попали в message flow',
        value: total,
        base: buffered || total,
      },
      {
        label: 'Принято в обработку',
        value: accepted,
        base: total || accepted,
      },
      {
        label: 'С проектом (чат привязан к проекту)',
        value: withProjects,
        base: accepted || withProjects,
      },
      {
        label: 'Приняты, но без проекта',
        value: withoutProjects,
        base: accepted || withoutProjects,
      },
      {
        label: 'Отброшены фильтрами',
        value: rejected,
        base: total || rejected,
      },
      {
        label: 'Поставлены в очередь report-групп',
        value: forwarded,
        base: withProjects || forwarded,
        note: `источник: ${reportForwardedSourceLabel}`,
      },
    ];
  }, [flowSummaryTotals, handlerFlowTotals, reportForwardedEstimate, reportForwardedSourceLabel]);

  const stageDropRows = useMemo(() => ([
    {
      label: 'Всего TDLib updates (message + service)',
      value: Number(handlerFlowTotals.updates || 0) + Number(handlerFlowTotals.nonMessageUpdates || 0),
      base: Number(handlerFlowTotals.updates || 0) + Number(handlerFlowTotals.nonMessageUpdates || 0),
    },
    {
      label: 'Не message updates (служебные апдейты)',
      value: Number(handlerFlowTotals.nonMessageUpdates || 0),
      base: Number(handlerFlowTotals.updates || 0) + Number(handlerFlowTotals.nonMessageUpdates || 0),
    },
    {
      label: 'Пропущены: собственные сообщения',
      value: Number(handlerFlowTotals.skippedOwnMessages || 0),
      base: Number(handlerFlowTotals.updates || 0) + Number(handlerFlowTotals.nonMessageUpdates || 0),
    },
    {
      label: 'Пропущены: неактивный аккаунт',
      value: Number(handlerFlowTotals.skippedInactiveAccount || 0),
      base: Number(handlerFlowTotals.updates || 0) + Number(handlerFlowTotals.nonMessageUpdates || 0),
    },
    {
      label: 'Пропущены: нет TDLib-клиента',
      value: Number(handlerFlowTotals.skippedMissingClient || 0),
      base: Number(handlerFlowTotals.updates || 0) + Number(handlerFlowTotals.nonMessageUpdates || 0),
    },
    {
      label: 'Отбраковано в flow фильтрами',
      value: Number(flowSummaryTotals.rejected || 0),
      base: Number(flowSummaryTotals.total || 0),
    },
    {
      label: 'Приняты, но без проекта',
      value: Number(flowSummaryTotals.withoutProjects || 0),
      base: Number(flowSummaryTotals.accepted || 0),
    },
    {
      label: 'Не дошли до report-группы после этапа "с проектом"',
      value: notReachedReportAfterProjectStage,
      base: Number(flowSummaryTotals.withProjects || 0),
    },
    {
      label: 'Не прошли keyword-match (по project checks)',
      value: Math.max(0, Number(projectFlow.totalChecks || 0) - Number(projectFlow.keywordMatched || 0)),
      base: Number(projectFlow.totalChecks || 0),
    },
  ]), [handlerFlowTotals, flowSummaryTotals, notReachedReportAfterProjectStage, projectFlow]);

  const orderedFilterSteps = useMemo(() => {
    const updates = Number(handlerFlowTotals.updates || 0);
    const nonMessageUpdates = Number(handlerFlowTotals.nonMessageUpdates || 0);
    const allUpdates = updates + nonMessageUpdates;
    const buffered = Number(handlerFlowTotals.buffered || 0);
    const accepted = Number(flowSummaryTotals.accepted || 0);
    const withProjects = Number(flowSummaryTotals.withProjects || 0);

    const reason = (key) => Number(flowSummaryTotals.rejectedByReason?.[key] || 0);

    return [
      {
        kind: 'pass',
        title: '1) all updates (TDLib)',
        value: allUpdates,
        base: allUpdates,
        description: 'Все апдейты TDLib: message + service.',
      },
      {
        kind: 'pass',
        title: '2) updateNewMessage',
        value: updates,
        base: allUpdates,
        description: 'Часть all updates, которая является сообщениями.',
      },
      {
        kind: 'drop',
        title: '3) Отсев: nonMessageUpdates',
        value: nonMessageUpdates,
        base: allUpdates,
        description: 'Апдейты Telegram не про сообщения (служебные update-типы).',
      },
      {
        kind: 'drop',
        title: '4) Отсев: skippedMissingClient',
        value: Number(handlerFlowTotals.skippedMissingClient || 0),
        base: allUpdates,
        description: 'У апдейта не найден живой TDLib-клиент.',
      },
      {
        kind: 'drop',
        title: '5) Отсев: skippedInactiveAccount',
        value: Number(handlerFlowTotals.skippedInactiveAccount || 0),
        base: allUpdates,
        description: 'Аккаунт не в активном runtime-состоянии.',
      },
      {
        kind: 'drop',
        title: '6) Отсев: skippedOwnMessages',
        value: Number(handlerFlowTotals.skippedOwnMessages || 0),
        base: allUpdates,
        description: 'Собственные исходящие и служебные flow-маркеры.',
      },
      {
        kind: 'pass',
        title: '7) Буфер messageBuffer.addMessage',
        value: buffered,
        base: updates,
        description: 'Сообщения дошли до буфера и дальше проходят фильтры messageBuffer.',
      },
      {
        kind: 'drop',
        title: '8) Фильтр: flow_debug_message',
        value: reason('flow_debug_message'),
        base: buffered,
        description: FLOW_REASON_DETAILS.flow_debug_message,
      },
      {
        kind: 'drop',
        title: '9) Фильтр: invalid_sender',
        value: reason('invalid_sender'),
        base: buffered,
        description: FLOW_REASON_DETAILS.invalid_sender,
      },
      {
        kind: 'drop',
        title: '10) Фильтр: non_group_chat',
        value: reason('non_group_chat'),
        base: buffered,
        description: FLOW_REASON_DETAILS.non_group_chat,
      },
      {
        kind: 'drop',
        title: '11) Фильтр: invalid_text',
        value: reason('invalid_text'),
        base: buffered,
        description: FLOW_REASON_DETAILS.invalid_text,
      },
      {
        kind: 'drop',
        title: '12) Фильтр: invalid_length',
        value: reason('invalid_length'),
        base: buffered,
        description: FLOW_REASON_DETAILS.invalid_length,
      },
      {
        kind: 'drop',
        title: '13) Фильтр: blacklist_match',
        value: reason('blacklist_match'),
        base: Number(flowSummaryTotals.total || 0),
        description: FLOW_REASON_DETAILS.blacklist_match,
      },
      {
        kind: 'pass',
        title: '14) accepted',
        value: accepted,
        base: Number(flowSummaryTotals.total || accepted),
        description: 'Сообщения прошли фильтры и приняты к проектной обработке.',
      },
      {
        kind: 'pass',
        title: '15) withProjects',
        value: withProjects,
        base: accepted,
        description: 'У сообщения найден(ы) проект(ы), где этот чат подписан.',
      },
      {
        kind: 'pass',
        title: '16) keywordMatched (по project checks)',
        value: projectFlow.keywordMatched,
        base: withProjects || projectFlow.keywordMatched,
        description: 'Keyword-engine нашёл совпадение (1 сообщение может проверяться в нескольких проектах).',
      },
      {
        kind: 'drop',
        title: '17) withoutProjects',
        value: Number(flowSummaryTotals.withoutProjects || 0),
        base: accepted,
        description: 'Сообщение принято, но для чата нет активного проекта.',
      },
      {
        kind: 'pass',
        title: '18) report-group delivery (queued)',
        value: reportForwardedEstimate,
        base: projectFlow.keywordMatched || withProjects || reportForwardedEstimate,
        description: 'Совпавшее сообщение поставлено в очередь отправки в report-группу.',
      },
      {
        kind: 'pass',
        title: '19) report-group delivery (sent)',
        value: projectFlow.deliverySent,
        base: reportForwardedEstimate || projectFlow.deliverySent,
        description: 'Очередь успешно отправила в Telegram report-группы.',
      },
      {
        kind: 'drop',
        title: '20) send_failed',
        value: projectFlow.deliveryFailed,
        base: reportForwardedEstimate || 0,
        description: 'Ошибка отправки после ретраев очереди.',
      },
      {
        kind: 'drop',
        title: '21) duplicate_message',
        value: projectFlow.routedDuplicate,
        base: projectFlow.keywordMatched || 0,
        description: 'Совпадение найдено, но отправка отброшена дедупликацией очереди.',
      },
      {
        kind: 'drop',
        title: '22) matched_but_not_routed',
        value: projectFlow.matchedButNotRouted,
        base: projectFlow.keywordMatched || 0,
        description: 'Ключи совпали, но нет валидной маршрутизации (bot/report_group).',
      },
    ];
  }, [handlerFlowTotals, flowSummaryTotals, reportForwardedEstimate, projectFlow]);

  const invalidSenderTypeRows = useMemo(() => {
    const rows = Object.entries(invalidSenderByType)
      .map(([senderType, value]) => ({
        senderType,
        value: Number(value || 0),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);

    const total = rows.reduce((sum, row) => sum + row.value, 0);
    return rows.map((row) => ({
      ...row,
      pct: total > 0 ? row.value / total : 0,
      description: INVALID_SENDER_TYPE_DETAILS[row.senderType] || 'Другой тип sender_id из TDLib.',
    }));
  }, [invalidSenderByType]);

  const projectFailReasonRows = useMemo(() => (
    Object.entries(projectFlow.failedByReason || {})
      .map(([reason, value]) => ({
        reason,
        value: Number(value || 0),
        reasonLabel: getProjectFlowReasonLabel(reason),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 12)
  ), [projectFlow]);

  const handlerFlowSummarySource = useMemo(() => {
    if (!handlerFlowSummary) {
      return 'нет данных';
    }

    if (handlerFlowSummary?.timeline?.updates && Array.isArray(handlerFlowSummary.timeline.updates) && handlerFlowSummary.timeline.updates.length > 0) {
      return `за последние ${handlerFlowSummary?.windowParams?.hours || windowHours} ч`;
    }

    return `кумулятивно: ${formatK(handlerFlowSummary.cumulative?.updates || 0)}`;
  }, [handlerFlowSummary, windowHours]);

  const recentMatchedEvents = useMemo(() => (
    (projectFlow.recentMatches || [])
      .slice()
      .reverse()
      .map((item) => {
        const primary = Array.isArray(item.primaryKeywords) ? item.primaryKeywords : [];
        const secondary = Array.isArray(item.secondaryKeywords) ? item.secondaryKeywords : [];
        const keywords = [...primary, ...secondary].slice(0, 8);
        const sourceDateLabel = item.sourceMessageDateIso
          ? new Date(item.sourceMessageDateIso).toLocaleString()
          : '—';
        const deliveryDateLabel = item.deliveryAt
          ? new Date(item.deliveryAt).toLocaleString()
          : null;
        const reportMessageLink = buildTelegramChatMessageLink(item.reportGroupId, item.telegramReportMessageId);
        const statusMeta = getMatchStatusMeta(item.status);

        return {
          ...item,
          statusKey: item.status || 'unknown',
          keywords,
          statusLabel: statusMeta.label,
          statusDescription: statusMeta.description,
          reasonLabel: getProjectFlowReasonLabel(item.reason),
          eventTimeLabel: item.ts_iso ? new Date(item.ts_iso).toLocaleString() : '—',
          sourceDateLabel,
          deliveryDateLabel,
          reportMessageLink,
          hasKeywordMatch: hasKeywordMatchInEvent({
            statusKey: item.status || 'unknown',
            reason: item.reason,
            keywords,
          }),
        };
      })
  ), [projectFlow.recentMatches]);

  const groupedRecentMatchedEvents = useMemo(() => {
    const groups = new Map();

    for (const item of recentMatchedEvents) {
      const groupingKey = `${item.chatId || 'unknown'}:${item.messageId || `nomsg-${item.ts || 0}-${item.projectId || 0}`}`;
      const itemTs = Number(item.ts || 0);
      const fallbackSourceLink = item.sourceMessageLink || buildTelegramChatMessageLink(item.chatId, item.messageId);

      if (!groups.has(groupingKey)) {
        groups.set(groupingKey, {
          groupingKey,
          chatId: item.chatId || null,
          messageId: item.messageId || null,
          eventTs: itemTs,
          eventTimeLabel: item.eventTimeLabel,
          sourceDateLabel: item.sourceDateLabel,
          sourceMessageLink: fallbackSourceLink,
          messagePreview: item.messagePreview || '',
          hasKeywordMatch: Boolean(item.hasKeywordMatch),
          projectsChecked: 0,
          projectNames: [],
          statusCounts: {},
          reasonCounts: {},
          keywordsSet: new Set(),
          reportGroupIds: new Set(),
          routeSources: new Set(),
        });
      }

      const group = groups.get(groupingKey);
      group.projectsChecked += 1;
      group.hasKeywordMatch = group.hasKeywordMatch || Boolean(item.hasKeywordMatch);

      if (item.projectName && !group.projectNames.includes(item.projectName) && group.projectNames.length < 12) {
        group.projectNames.push(item.projectName);
      }

      const statusKey = item.statusKey || 'unknown';
      group.statusCounts[statusKey] = (group.statusCounts[statusKey] || 0) + 1;

      if (item.reason) {
        group.reasonCounts[item.reason] = (group.reasonCounts[item.reason] || 0) + 1;
      }

      if (Array.isArray(item.keywords)) {
        for (const keyword of item.keywords) {
          if (group.keywordsSet.size >= 12) {
            break;
          }
          group.keywordsSet.add(keyword);
        }
      }

      if (item.reportGroupId) {
        group.reportGroupIds.add(String(item.reportGroupId));
      }
      if (item.routeSource) {
        group.routeSources.add(String(item.routeSource));
      }

      if (itemTs >= group.eventTs) {
        group.eventTs = itemTs;
        group.eventTimeLabel = item.eventTimeLabel;
        group.sourceDateLabel = item.sourceDateLabel;
        group.messagePreview = item.messagePreview || group.messagePreview;
        group.sourceMessageLink = fallbackSourceLink || group.sourceMessageLink;
      }
    }

    return Array.from(groups.values())
      .map((group) => {
        const statusBreakdown = Object.entries(group.statusCounts)
          .sort((a, b) => b[1] - a[1])
          .map(([status, count]) => `${getMatchStatusMeta(status).label}: ${formatK(count)}`);
        const reasonBreakdown = Object.entries(group.reasonCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => `${getProjectFlowReasonLabel(reason)}: ${formatK(count)}`);

        return {
          ...group,
          statusBreakdown,
          reasonBreakdown,
          keywords: Array.from(group.keywordsSet),
          reportGroupIds: Array.from(group.reportGroupIds),
          routeSources: Array.from(group.routeSources),
        };
      })
      .sort((a, b) => b.eventTs - a.eventTs);
  }, [recentMatchedEvents]);

  const recentItemsByMode = useMemo(() => (
    recentViewMode === 'messages'
      ? groupedRecentMatchedEvents
      : recentMatchedEvents
  ), [recentViewMode, groupedRecentMatchedEvents, recentMatchedEvents]);

  const keywordFilteredRecentItems = useMemo(() => (
    onlyKeywordPassed
      ? recentItemsByMode.filter((item) => Boolean(item.hasKeywordMatch))
      : recentItemsByMode
  ), [recentItemsByMode, onlyKeywordPassed]);

  const recentStatusCounts = useMemo(() => {
    return keywordFilteredRecentItems.reduce((acc, item) => {
      if (recentViewMode === 'messages') {
        Object.keys(item.statusCounts || {}).forEach((status) => {
          acc[status] = (acc[status] || 0) + 1;
        });
        return acc;
      }

      const key = item.statusKey || 'unknown';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [keywordFilteredRecentItems, recentViewMode]);

  const recentStatusOptions = useMemo(() => {
    const knownStatuses = Object.keys(MATCH_STATUS_META);
    const extraStatuses = Object.keys(recentStatusCounts)
      .filter((status) => !knownStatuses.includes(status))
      .sort();

    return [
      {
        key: 'all',
        label: 'Все статусы',
        description: recentViewMode === 'messages'
          ? 'Все исходные сообщения (каждая карточка объединяет проверки по проектам).'
          : 'Все события project-check и доставки.',
        count: keywordFilteredRecentItems.length,
      },
      ...knownStatuses.map((status) => ({
        key: status,
        ...getMatchStatusMeta(status),
        count: recentStatusCounts[status] || 0,
      })),
      ...extraStatuses.map((status) => ({
        key: status,
        ...getMatchStatusMeta(status),
        count: recentStatusCounts[status] || 0,
      })),
    ];
  }, [keywordFilteredRecentItems.length, recentStatusCounts, recentViewMode]);

  const filteredRecentItems = useMemo(() => (
    recentStatusFilter === 'all'
      ? keywordFilteredRecentItems
      : keywordFilteredRecentItems.filter((item) => {
        if (recentViewMode === 'messages') {
          return Number(item.statusCounts?.[recentStatusFilter] || 0) > 0;
        }
        return item.statusKey === recentStatusFilter;
      })
  ), [keywordFilteredRecentItems, recentStatusFilter, recentViewMode]);

  const recentPagination = useMemo(() => {
    const totalItems = filteredRecentItems.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / recentPageSize));
    const currentPage = Math.min(recentPage, totalPages);
    const startIndex = (currentPage - 1) * recentPageSize;
    const endIndexExclusive = startIndex + recentPageSize;
    const pagedItems = filteredRecentItems.slice(startIndex, endIndexExclusive);

    return {
      items: pagedItems,
      totalItems,
      totalPages,
      currentPage,
      from: totalItems === 0 ? 0 : startIndex + 1,
      to: Math.min(totalItems, endIndexExclusive),
    };
  }, [filteredRecentItems, recentPage, recentPageSize]);

  const handleTraceMessageByLink = async () => {
    const link = traceLinkInput.trim();
    if (!link) {
      setTraceError('Вставьте ссылку на сообщение Telegram.');
      setTraceData(null);
      return;
    }

    try {
      setTraceLoading(true);
      setTraceError(null);
      const response = await traceMessageFlowByLink(link);
      setTraceData(response?.trace || null);
    } catch (traceRequestError) {
      setTraceData(null);
      setTraceError(
        traceRequestError?.response?.data?.error
          || traceRequestError?.message
          || 'Не удалось построить трассировку'
      );
    } finally {
      setTraceLoading(false);
    }
  };

  const handleFlushTraceBuffer = async () => {
    try {
      setTraceActionLoading(true);
      setTraceActionError(null);
      setTraceActionSuccess(null);

      const response = await flushMessageBuffer(
        traceLinkInput.trim()
          ? `trace_flush:${traceLinkInput.trim().slice(0, 180)}`
          : 'trace_flush_manual'
      );

      const flush = response?.flush || {};
      setTraceActionSuccess(
        [
          'messageBuffer сброшен.',
          `дропнуто из очереди: ${formatK(flush.droppedPending || 0)}`,
          flush.flushWillInterruptCurrentBatch
            ? `текущий batch будет прерван после текущего чанка (ещё ~${formatK(flush.currentBatchRemaining || 0)} сообщений в нём)`
            : 'текущий batch не был активен',
        ].join(' ')
      );

      if (traceLinkInput.trim()) {
        const responseAfterFlush = await traceMessageFlowByLink(traceLinkInput.trim());
        setTraceData(responseAfterFlush?.trace || null);
      }
    } catch (flushError) {
      setTraceActionError(
        flushError?.response?.data?.error
          || flushError?.message
          || 'Не удалось сбросить messageBuffer'
      );
    } finally {
      setTraceActionLoading(false);
    }
  };

  const handleRunDeliveryTest = async (projectId) => {
    if (!projectId) {
      return;
    }

    setDeliveryTestByProject((prev) => ({
      ...prev,
      [projectId]: {
        loading: true,
        error: null,
        result: null,
      },
    }));

    try {
      const response = await sendReportDeliveryTest({ projectId });
      setDeliveryTestByProject((prev) => ({
        ...prev,
        [projectId]: {
          loading: false,
          error: null,
          result: response?.deliveryTest || null,
        },
      }));
    } catch (deliveryError) {
      setDeliveryTestByProject((prev) => ({
        ...prev,
        [projectId]: {
          loading: false,
          error:
            deliveryError?.response?.data?.error
            || deliveryError?.message
            || 'Не удалось отправить тестовое сообщение',
          result: null,
        },
      }));
    }
  };

  const traceVerdict = useMemo(
    () => (traceData ? getTraceVerdict(traceData) : null),
    [traceData]
  );

  const traceMatchedProjectNames = useMemo(() => (
    Array.isArray(traceData?.stageSummary?.keywordChecks?.matchedProjectNames)
      ? traceData.stageSummary.keywordChecks.matchedProjectNames
      : []
  ), [traceData]);

  const traceMatchedProjectDetails = useMemo(() => (
    Array.isArray(traceData?.stageSummary?.keywordChecks?.matchedProjectDetails)
      ? traceData.stageSummary.keywordChecks.matchedProjectDetails
      : []
  ), [traceData]);

  const traceHasBufferedPending = Boolean(traceData?.stageSummary?.buffer?.pending);
  const traceBufferRuntime = traceData?.stageSummary?.buffer?.runtime || null;

  const traceDeliveryRows = useMemo(() => {
    const entries = Object.entries(traceData?.stageSummary?.delivery?.runtimeByStatus || {})
      .map(([status, count]) => ({
        status,
        count: Number(count || 0),
        meta: getMatchStatusMeta(status),
      }))
      .filter((item) => item.count > 0)
      .sort((a, b) => b.count - a.count);

    return entries;
  }, [traceData]);

  const traceRecentProjectEvents = useMemo(() => (
    Array.isArray(traceData?.runtimeEvidence?.recentProjectEvents)
      ? traceData.runtimeEvidence.recentProjectEvents.slice(0, 8)
      : []
  ), [traceData]);

  return (
    <Card title="Message Health Monitor" className="lg:col-span-3" loading={isLoading}>
      {error ? (
        <p className="text-sm text-red-700">
          Ошибка загрузки Message Health Monitor:&nbsp;
          {error?.response?.data?.error || error.message || 'unknown'}
        </p>
      ) : !payload ? (
        <p className="text-sm text-gray-500">Нет данных от /backend/metrics/ops-monitor.</p>
      ) : null}

      {payload && (
        <>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <label className="flex min-w-[220px] flex-col text-xs font-medium text-gray-600">
              Window: <span className="font-semibold text-gray-800">{windowHours}h</span>
              <input
                type="range"
                min={6}
                max={72}
                step={6}
                value={windowHours}
                onChange={(event) => setWindowHours(Number(event.target.value))}
                className="mt-1 h-2 cursor-pointer appearance-none rounded-lg bg-gray-200"
              />
            </label>

            <div className="ml-auto flex gap-2">
              {processedDrop?.alert ? (
                <Badge variant="red" size="sm">
                  <span className="inline-flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3 w-3" />
                    Processed drop {formatPct(processedDrop.dropPct)}
                  </span>
                </Badge>
              ) : (
                <Badge variant="green" size="sm">Processed stable</Badge>
              )}

              {matchedDrop?.alert ? (
                <Badge variant="red" size="sm">
                  <span className="inline-flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-3 w-3" />
                    Matched drop {formatPct(matchedDrop.dropPct)}
                  </span>
                </Badge>
              ) : (
                <Badge variant="green" size="sm">Matched stable</Badge>
              )}
            </div>
          </div>

          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Processed</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {formatK(displaySummary.totalProcessed)}
              </p>
              <p className="mt-1 text-[11px] text-gray-500">
                источник: {summarizeDataSource(displaySummary.sourceProcessed)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Matched</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {formatK(displaySummary.totalMatched)}
              </p>
              <p className="mt-1 text-[11px] text-gray-500">
                источник: {summarizeDataSource(displaySummary.sourceMatched)}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Match rate</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{formatPct(displaySummary.matchRate || 0)}</p>
            </div>
          </div>

          <div className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Всего по flow</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{formatK(flowSummaryTotals.total)}</p>
              <p className="mt-1 text-xs text-gray-500">Принятые + отбраковка ({flowSummaryReasonLabel})</p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">Принято / Отброшено</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {formatK(flowSummaryTotals.accepted)} / {formatK(flowSummaryTotals.rejected)}
              </p>
              <p className="mt-1 text-xs text-gray-500">Итого по message flow</p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">С проектом / Без проекта</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {formatK(flowSummaryTotals.withProjects)} / {formatK(flowSummaryTotals.withoutProjects)}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Mark read failures: {formatK(flowSummaryTotals.markReadFailures)}
              </p>
            </div>
          </div>

          {hasNoData ? (
            <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
              Сейчас пришло ноль статистики. Проверьте, что продакшн пишет в
              <code className="mx-1 rounded bg-yellow-100 px-1 py-0.5 font-mono">metricsService.incrementMessagesProcessed()</code>
              и/или что включён потоковый буфер для /backend/metrics/flowSummary в runtime.
            </div>
          ) : null}

          <div className="mb-5 rounded-md border border-gray-200 p-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Диагностика системы</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{diagnosis?.message || 'Диагностика недоступна'}</p>
            <p className="mt-1 text-xs text-gray-600">Статус: {diagnosis?.status || 'unknown'}</p>
          </div>

          <div className="mb-6 rounded-md border border-gray-200 p-3">
            <p className="text-sm font-semibold text-gray-800">Трассировка одного сообщения по ссылке</p>
            <p className="mt-1 text-xs text-gray-500">
              Вставьте ссылку вида https://t.me/username/12345 или https://t.me/c/123456/789 и проверьте, где сообщение отсеялось.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={traceLinkInput}
                onChange={(event) => setTraceLinkInput(event.target.value)}
                placeholder="https://t.me/..."
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleTraceMessageByLink}
                disabled={traceLoading}
                className="rounded border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {traceLoading ? 'Проверяем...' : 'Трассировать'}
              </button>
              <button
                type="button"
                onClick={handleFlushTraceBuffer}
                disabled={traceActionLoading}
                className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {traceActionLoading ? 'Сбрасываем...' : 'Сбросить messageBuffer'}
              </button>
            </div>

            {traceError ? (
              <p className="mt-2 text-xs text-red-700">{traceError}</p>
            ) : null}

            {traceActionError ? (
              <p className="mt-2 text-xs text-red-700">{traceActionError}</p>
            ) : null}

            {traceActionSuccess ? (
              <p className="mt-2 text-xs text-emerald-700">{traceActionSuccess}</p>
            ) : null}

            {traceData ? (
              <div className="mt-3 space-y-2 rounded border border-gray-200 bg-gray-50 p-3">
                {traceVerdict ? (
                  <div className={`rounded border p-3 ${getTraceVerdictClasses(traceVerdict.tone)}`}>
                    <p className="text-sm font-semibold">{traceVerdict.title}</p>
                    <p className="mt-1 text-xs">{traceVerdict.description}</p>
                  </div>
                ) : null}

                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Источник сообщения</p>
                    <p className="mt-1 text-xs text-gray-600">
                      chat_id: {traceData?.resolvedChat?.chatId || 'не определён'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      источник определения: {traceData?.resolvedChat?.source || '—'}
                    </p>
                    <p className="mt-2 text-xs text-gray-500">
                      Кандидаты message_id: {Array.isArray(traceData?.messageIdCandidates)
                        ? traceData.messageIdCandidates.map((item) => `${item.value} (${item.source})`).join(', ')
                        : '—'}
                    </p>
                  </div>

                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Flow stage</p>
                    <p className="mt-1 text-xs text-gray-600">
                      {traceData?.stageSummary?.handler?.reasonLabel || traceData?.stageSummary?.handler?.reason || '—'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      статус: {traceData?.stageSummary?.handler?.status || 'unknown'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Project check</p>
                    <p className="mt-1 text-xs text-gray-600">
                      проверено проектов: {formatK(traceData?.stageSummary?.keywordChecks?.checkedProjects || 0)}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      совпало проектов: {formatK(traceData?.stageSummary?.keywordChecks?.matchedProjects || 0)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      к чату привязано: {formatK(traceData?.stageSummary?.projectBinding?.activeProjectsCount || 0)}
                    </p>
                  </div>

                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Очередь / доставка</p>
                    <p className="mt-1 text-xs text-gray-600">
                      runtime событий: {formatK(traceData?.stageSummary?.delivery?.runtimeEventsCount || 0)}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      db match-записей: {formatK(traceData?.stageSummary?.delivery?.dbMatchesCount || 0)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      итог после keyword-match / очереди / отправки
                    </p>
                  </div>

                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Буфер</p>
                    <p className="mt-1 text-xs text-gray-600">
                      в messageBuffer: {traceData?.stageSummary?.buffer?.pending ? 'да' : 'нет'}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      pending count: {formatK(traceData?.stageSummary?.buffer?.pendingCount || 0)}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      всего в очереди сейчас: {formatK(traceBufferRuntime?.queuedMessages || 0)}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">
                      приоритетных debug-сообщений: {formatK(traceBufferRuntime?.priorityQueuedMessages || 0)}
                    </p>
                    {traceBufferRuntime?.currentBatch ? (
                      <p className="mt-1 text-xs text-gray-500">
                        текущий batch: {formatK(traceBufferRuntime.currentBatch.totalMessages || 0)}
                        {' · '}
                        обработано: {formatK(traceBufferRuntime.currentBatch.processedMessages || 0)}
                        {' · '}
                        длится: {formatK(Math.round((traceBufferRuntime.currentBatch.ageMs || 0) / 1000))} сек
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        batch сейчас не выполняется
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      если тут «да», значит сообщение ещё не дошло до processProjectMessage
                    </p>
                  </div>
                </div>

                <div className="rounded border border-gray-200 bg-white p-2">
                  <p className="text-xs font-semibold text-gray-700">Совпавшие проекты</p>
                  {traceMatchedProjectNames.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {traceMatchedProjectNames.map((projectName) => (
                        <span
                          key={projectName}
                          className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] text-emerald-700"
                        >
                          {projectName}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">Нет финально совпавших проектов.</p>
                  )}
                </div>

                {traceData?.fetchedMessage?.found ? (
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Текст сообщения</p>
                    <p className="mt-1 text-xs text-gray-600">{traceData?.fetchedMessage?.textPreview || '—'}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      len={traceData?.fetchedMessage?.textLength || 0} · senderType={traceData?.fetchedMessage?.senderType || '—'} · content={traceData?.fetchedMessage?.contentType || '—'}
                    </p>
                  </div>
                ) : (
                  <p className="text-xs text-yellow-700">
                    Сообщение не удалось получить через TDLib (возможно аккаунты не видят этот чат/сообщение), но runtime/db следы всё равно показаны.
                  </p>
                )}

                {traceDeliveryRows.length > 0 ? (
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Статусы доставки по этому сообщению</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {traceDeliveryRows.map((item) => (
                        <span
                          key={item.status}
                          className={`rounded-full px-2 py-1 text-[11px] ${getMatchStatusBadgeClass(item.status)}`}
                        >
                          {item.meta.label}: {formatK(item.count)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {traceMatchedProjectDetails.length > 0 ? (
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">
                      {traceHasBufferedPending
                        ? 'Предварительный прогноз после выхода из буфера'
                        : 'Точный этап по совпавшим проектам'}
                    </p>
                    {traceHasBufferedPending ? (
                      <p className="mt-1 text-xs text-amber-700">
                        Сейчас 100% точный этап — messageBuffer. Ни совпадение по ключам, ни matched keyword ниже ещё не подтверждены реальным processProjectMessage:
                        это replay по текущим настройкам, чтобы понять, что вероятнее всего произойдёт после выхода из буфера.
                      </p>
                    ) : null}
                    <div className="mt-2 space-y-2">
                      {traceMatchedProjectDetails.map((item) => (
                        <div key={`${item.projectId}-${item.messageKey || item.projectName}`} className="rounded border border-gray-200 p-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold text-gray-800">{item.projectName}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[11px] ${getTraceVerdictClasses(getTraceVerdict({ exactStage: item.finalStage }).tone)}`}>
                              {item.finalStage?.label || '—'}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRunDeliveryTest(item.projectId)}
                              disabled={deliveryTestByProject[item.projectId]?.loading}
                              className="ml-auto rounded border border-blue-300 bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {deliveryTestByProject[item.projectId]?.loading ? 'Тестируем...' : 'Тест бота'}
                            </button>
                          </div>

                          <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                            <div>
                              <p className="text-xs text-gray-600">
                                {traceHasBufferedPending ? 'replay keyword' : 'matched keyword'}: {item.matchedKeyword || '—'}
                              </p>
                              <p className="mt-1 text-xs text-gray-600">
                                primary: {Array.isArray(item.primaryKeywords) && item.primaryKeywords.length > 0 ? item.primaryKeywords.join(', ') : '—'}
                              </p>
                              <p className="mt-1 text-xs text-gray-600">
                                secondary: {Array.isArray(item.secondaryKeywords) && item.secondaryKeywords.length > 0 ? item.secondaryKeywords.join(', ') : '—'}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-600">
                                route source: {item.routeSource || '—'}
                              </p>
                              <p className="mt-1 text-xs text-gray-600">
                                report group: {item.reportGroupId || '—'}
                              </p>
                              <p className="mt-1 text-xs text-gray-600">
                                bot token: {item.botTokenPresent ? 'есть' : 'нет'}
                              </p>
                            </div>
                          </div>

                          {deliveryTestByProject[item.projectId]?.error ? (
                            <div className="mt-2 rounded border border-red-100 bg-red-50 p-2">
                              <p className="text-xs text-red-700">
                                Тест бота не удался: {deliveryTestByProject[item.projectId]?.error}
                              </p>
                            </div>
                          ) : null}

                          {deliveryTestByProject[item.projectId]?.result ? (
                            <div className="mt-2 rounded border border-emerald-100 bg-emerald-50 p-2">
                              <p className="text-xs text-emerald-700">
                                Тест отправки прошёл: report group {deliveryTestByProject[item.projectId]?.result?.reportGroupId || '—'}
                              </p>
                              <p className="mt-1 text-xs text-emerald-700">
                                message id: {deliveryTestByProject[item.projectId]?.result?.telegramMessageId || '—'} · {deliveryTestByProject[item.projectId]?.result?.testedAt ? new Date(deliveryTestByProject[item.projectId].result.testedAt).toLocaleString() : ''}
                              </p>
                            </div>
                          ) : null}

                          {item.queueStatus ? (
                            <div className="mt-2 rounded border border-gray-100 bg-gray-50 p-2">
                              <p className="text-xs text-gray-600">
                                queue status: {item.queueStatus.status || '—'}{item.queueStatus.inQueue ? ' · в очереди сейчас' : ''}
                              </p>
                              {item.queueStatus.error ? (
                                <p className="mt-1 text-xs text-red-700">
                                  ошибка очереди: {item.queueStatus.error}
                                </p>
                              ) : null}
                            </div>
                          ) : null}

                          {item.latestPersistedEvent ? (
                            <div className="mt-2 rounded border border-blue-100 bg-blue-50 p-2">
                              <p className="text-xs text-blue-700">
                                persistent trace: {item.latestPersistedEvent.status || '—'} · {item.latestPersistedEvent.created_at ? new Date(item.latestPersistedEvent.created_at).toLocaleString() : 'время неизвестно'}
                              </p>
                              {item.latestPersistedEvent.reason ? (
                                <p className="mt-1 text-xs text-blue-700">
                                  причина: {getProjectFlowReasonLabel(item.latestPersistedEvent.reason) || item.latestPersistedEvent.reason}
                                </p>
                              ) : null}
                              {item.latestPersistedEvent.telegram_report_message_id ? (
                                <p className="mt-1 text-xs text-blue-700">
                                  report message id: {item.latestPersistedEvent.telegram_report_message_id}
                                </p>
                              ) : null}
                            </div>
                          ) : null}

                          <p className="mt-2 text-xs text-gray-500">
                            runtime events: {formatK(item.runtimeEventsCount || 0)} · persistent db events: {formatK(item.persistedEventsCount || 0)} · db matches: {formatK(item.dbMatchesCount || 0)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {traceData?.stageSummary?.keywordChecks?.rejectedReasons && Object.keys(traceData.stageSummary.keywordChecks.rejectedReasons).length > 0 ? (
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Причины отсева на project-check</p>
                    <div className="mt-1 space-y-1">
                      {Object.entries(traceData.stageSummary.keywordChecks.rejectedReasons)
                        .sort((a, b) => Number(b[1]) - Number(a[1]))
                        .slice(0, 6)
                        .map(([reason, count]) => (
                          <p key={reason} className="text-xs text-gray-600">
                            {getProjectFlowReasonLabel(reason)}: {formatK(count)}
                          </p>
                        ))}
                    </div>
                  </div>
                ) : null}

                {traceRecentProjectEvents.length > 0 ? (
                  <div className="rounded border border-gray-200 bg-white p-2">
                    <p className="text-xs font-semibold text-gray-700">Последние runtime-события по этому сообщению</p>
                    <div className="mt-2 space-y-2">
                      {traceRecentProjectEvents.map((item, index) => {
                        const statusMeta = getMatchStatusMeta(item?.status || 'unknown');
                        const reportLink = buildTelegramChatMessageLink(item?.reportGroupId, item?.telegramReportMessageId);
                        const sourceLink = item?.sourceMessageLink || buildTelegramChatMessageLink(item?.chatId, item?.messageId);

                        return (
                          <div key={`${item?.projectId || 'noproj'}-${item?.status || 'unknown'}-${item?.ts || index}`} className="rounded border border-gray-200 p-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[11px] ${getMatchStatusBadgeClass(item?.status)}`}>
                                {statusMeta.label}
                              </span>
                              <span className="text-xs font-medium text-gray-700">
                                {item?.projectName || `project#${item?.projectId || '—'}`}
                              </span>
                              <span className="text-xs text-gray-500">
                                {item?.ts_iso ? new Date(item.ts_iso).toLocaleString() : '—'}
                              </span>
                            </div>

                            <p className="mt-1 text-xs text-gray-600">
                              Причина: {getProjectFlowReasonLabel(item?.reason) || '—'}
                            </p>

                            {Array.isArray(item?.keywords) && item.keywords.length > 0 ? (
                              <p className="mt-1 text-xs text-gray-600">
                                Ключи: {item.keywords.join(', ')}
                              </p>
                            ) : null}

                            <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-500">
                              {sourceLink ? (
                                <a href={sourceLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                  Источник
                                </a>
                              ) : null}
                              {reportLink ? (
                                <a href={reportLink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                                  Report message
                                </a>
                              ) : null}
                              {item?.reportGroupId ? <span>report_group: {item.reportGroupId}</span> : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="mb-6 rounded-md border border-gray-200 p-3">
            <p className="mb-3 text-sm font-semibold text-gray-800">
              Message Handler summary
              <span className="ml-2 text-xs text-gray-500">({handlerFlowSummarySource})</span>
            </p>
            <div className="mt-1 grid grid-cols-1 gap-3 md:grid-cols-4 lg:grid-cols-7">
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">updates</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.updates)}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">buffered</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.buffered)}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">nonMessageUpdates</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.nonMessageUpdates)}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">skippedOwn</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.skippedOwnMessages)}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">skippedMissing</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.skippedMissingClient)}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">skippedInactive</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.skippedInactiveAccount)}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-2">
                <p className="text-xs text-gray-500">handlerErrors</p>
                <p className="text-sm font-semibold">{formatK(handlerFlowTotals.handlerErrors)}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="mb-2 text-sm font-semibold text-gray-800">
                Причины отбраковки сообщений
                {flowSummaryReasonLabel ? <span className="ml-2 text-xs text-gray-500">{flowSummaryReasonLabel}</span> : null}
              </p>
              <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="rounded border border-gray-200 p-2">
                  <p className="text-xs text-gray-500">Всего апдейтов (handler)</p>
                  <p className="text-sm font-semibold text-gray-900">{formatK(handlerFlowTotals.updates)}</p>
                </div>
                <div className="rounded border border-gray-200 p-2">
                  <p className="text-xs text-gray-500">Всего сообщений в flow</p>
                  <p className="text-sm font-semibold text-gray-900">{formatK(flowSummaryTotals.total)}</p>
                </div>
                <div className="rounded border border-gray-200 p-2">
                  <p className="text-xs text-gray-500">С проектом (чат привязан)</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatK(flowSummaryTotals.withProjects)} / {formatPct(flowSummaryTotals.total ? flowSummaryTotals.withProjects / flowSummaryTotals.total : 0)}
                  </p>
                </div>
                <div className="rounded border border-gray-200 p-2">
                  <p className="text-xs text-gray-500">Поставлены в очередь report-групп</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {formatK(reportForwardedEstimate)} / {formatPct(flowSummaryTotals.withProjects ? reportForwardedEstimate / flowSummaryTotals.withProjects : 0)}
                  </p>
                  <p className="text-[11px] text-gray-500">источник: {reportForwardedSourceLabel}</p>
                </div>
              </div>

              <div className="mb-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Этапы обработки (воронка)</p>
                {flowFunnelRows.map((row, index) => {
                  const prev = index > 0 ? Number(flowFunnelRows[index - 1]?.value || 0) : null;
                  const dropped = prev === null ? 0 : Math.max(0, prev - Number(row.value || 0));
                  const grown = prev === null ? false : Number(row.value || 0) > prev;
                  return (
                  <div key={row.label} className="flex items-center justify-between rounded border border-gray-200 p-2">
                    <div>
                      <span className="text-sm text-gray-800">{row.label}</span>
                      {row.note ? <p className="text-[11px] text-gray-500">{row.note}</p> : null}
                    </div>
                    <div className="text-right">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatK(row.value)} / {formatPct(row.base ? row.value / row.base : 0)}
                      </span>
                      {prev !== null ? (
                        <p className="text-[11px] text-gray-500">
                          {grown
                            ? `+${formatK(Number(row.value || 0) - prev)} vs prev`
                            : `отлетело: ${formatK(dropped)}`}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="mb-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Флоучарт фильтров (порядок как в коде)</p>
                {orderedFilterSteps.map((step, index) => (
                  <div key={step.title}>
                    <div className="rounded border border-gray-200 p-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                              step.kind === 'drop'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}
                          >
                            {step.kind === 'drop' ? 'DROP' : 'PASS'}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{step.title}</span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatK(step.value)} / {formatPct(step.base ? step.value / step.base : 0)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">{step.description}</p>
                    </div>
                    {index < orderedFilterSteps.length - 1 ? (
                      <div className="py-1 text-center text-gray-300">↓</div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="mb-3 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Где отлетает по этапам</p>
                {stageDropRows.map((row) => (
                  <div key={row.label} className="flex items-center justify-between rounded border border-gray-200 p-2">
                    <span className="text-sm text-gray-800">{row.label}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatK(row.value)} / {formatPct(row.base ? row.value / row.base : 0)}
                    </span>
                  </div>
                ))}
              </div>

              {detailedReasonRows.length > 0 ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2 text-[12px] text-gray-600">
                    <span>Причина</span>
                    <span className="text-right">% от отброшенных</span>
                    <span className="text-right">% от всех flow</span>
                    <span className="text-right">Что значит</span>
                  </div>
                  {detailedReasonRows.map((item) => (
                    <div key={item.label} className="grid grid-cols-4 items-center gap-2 rounded border border-gray-200 p-2">
                      <span className="text-sm text-gray-800">{item.label}: {formatK(item.value)}</span>
                      <span className="text-sm font-semibold text-gray-900 text-right">{formatPct(item.pctOfRejected)}</span>
                      <span className="text-sm font-semibold text-gray-900 text-right">{formatPct(item.pctOfTotal)}</span>
                      <span className="text-xs text-gray-500 text-right">{item.description}</span>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    Всего отбраковок: {formatK(flowSummaryTotals.rejected)} (размечено по причинам: {formatK(totalRejectedByReason)})
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">Пока нет причин отбраковки за выбранный период.</p>
              )}

              {invalidSenderTypeRows.length > 0 ? (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Расшифровка invalid_sender (кумулятивно)
                  </p>
                  <div className="grid grid-cols-3 gap-2 text-[12px] text-gray-600">
                    <span>sender_id._</span>
                    <span className="text-right">Количество / %</span>
                    <span className="text-right">Пояснение</span>
                  </div>
                  {invalidSenderTypeRows.map((row) => (
                    <div key={row.senderType} className="grid grid-cols-3 items-center gap-2 rounded border border-gray-200 p-2">
                      <span className="text-sm text-gray-800">{row.senderType}</span>
                      <span className="text-sm font-semibold text-gray-900 text-right">
                        {formatK(row.value)} / {formatPct(row.pct)}
                      </span>
                      <span className="text-xs text-gray-500 text-right">{row.description}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="mt-4 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Почему не дошли до report-группы (project checks)
                </p>
                <div className="grid grid-cols-2 gap-2 text-[12px] text-gray-600">
                  <span>Метрика</span>
                  <span className="text-right">Значение</span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">Всего проверок processProjectMessage</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">{formatK(projectFlow.totalChecks)}</span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">keywordMatched</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">
                    {formatK(projectFlow.keywordMatched)} / {formatPct(projectFlow.totalChecks ? projectFlow.keywordMatched / projectFlow.totalChecks : 0)}
                  </span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">queued в report-group</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">
                    {formatK(projectFlow.routedQueued)} / {formatPct(projectFlow.keywordMatched ? projectFlow.routedQueued / projectFlow.keywordMatched : 0)}
                  </span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">успешно отправлено (sent)</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">
                    {formatK(projectFlow.deliverySent)} / {formatPct(projectFlow.routedQueued ? projectFlow.deliverySent / projectFlow.routedQueued : 0)}
                  </span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">ошибки отправки (send_failed)</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">
                    {formatK(projectFlow.deliveryFailed)} / {formatPct(projectFlow.routedQueued ? projectFlow.deliveryFailed / projectFlow.routedQueued : 0)}
                  </span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">duplicate_message</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">
                    {formatK(projectFlow.routedDuplicate)} / {formatPct(projectFlow.keywordMatched ? projectFlow.routedDuplicate / projectFlow.keywordMatched : 0)}
                  </span>
                </div>
                <div className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                  <span className="text-sm text-gray-800">matched_but_not_routed</span>
                  <span className="text-sm font-semibold text-gray-900 text-right">
                    {formatK(projectFlow.matchedButNotRouted)} / {formatPct(projectFlow.keywordMatched ? projectFlow.matchedButNotRouted / projectFlow.keywordMatched : 0)}
                  </span>
                </div>

                {projectFailReasonRows.length > 0 ? (
                  <>
                    <p className="pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">Топ причин (reason)</p>
                    {projectFailReasonRows.map((item) => (
                      <div key={item.reason} className="grid grid-cols-2 items-center gap-2 rounded border border-gray-200 p-2">
                        <span className="text-sm text-gray-800">
                          {item.reasonLabel}
                          <span className="ml-1 text-xs text-gray-500">({item.reason})</span>
                        </span>
                        <span className="text-sm font-semibold text-gray-900 text-right">{formatK(item.value)}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="text-xs text-gray-500">Пока нет runtime-статистики причин на этапе project checks.</p>
                )}
              </div>
            </div>

            <div className="rounded-md border border-gray-200 p-3">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-gray-800">
                  События project-check и доставки в report-группы
                </p>
                <label className="text-xs text-gray-600">
                  На странице:
                  <select
                    className="ml-2 rounded border border-gray-300 px-2 py-1 text-xs"
                    value={recentPageSize}
                    onChange={(event) => {
                      setRecentPageSize(Number(event.target.value));
                      setRecentPage(1);
                    }}
                  >
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
              </div>

              <div className="mb-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRecentViewMode('messages');
                    setRecentPage(1);
                  }}
                  className={`rounded border px-2 py-1 text-xs ${
                    recentViewMode === 'messages'
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  По сообщениям
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRecentViewMode('events');
                    setRecentPage(1);
                  }}
                  className={`rounded border px-2 py-1 text-xs ${
                    recentViewMode === 'events'
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  По событиям
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOnlyKeywordPassed((prev) => !prev);
                    setRecentPage(1);
                  }}
                  className={`rounded border px-2 py-1 text-xs ${
                    onlyKeywordPassed
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {onlyKeywordPassed ? 'Только после keymatch: ON' : 'Только после keymatch: OFF'}
                </button>
              </div>

              <p className="mb-2 text-xs text-gray-500">
                {recentViewMode === 'messages'
                  ? 'Режим "По сообщениям": одна карточка = одно входящее сообщение; внутри показано, как оно прошло через проекты.'
                  : 'Режим "По событиям": одна карточка = одна проверка в конкретном проекте.'}
              </p>

              <p className="mb-3 text-xs text-gray-500">
                В runtime хранится до {formatK(projectFlow.recentMatches?.length || 0)} последних событий.
                Сейчас показано: {formatK(recentPagination.from)}–{formatK(recentPagination.to)} из {formatK(recentPagination.totalItems)}
                {recentViewMode === 'messages' ? ' сообщений' : ' событий'}.
              </p>

              <div className="mb-3 flex flex-wrap gap-2">
                {recentStatusOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setRecentStatusFilter(option.key);
                      setRecentPage(1);
                    }}
                    className={`rounded border px-2 py-1 text-xs ${
                      recentStatusFilter === option.key
                        ? 'border-blue-300 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}: {formatK(option.count)}
                  </button>
                ))}
              </div>

              {recentStatusFilter !== 'all' ? (
                <p className="mb-3 text-xs text-gray-500">
                  {getMatchStatusMeta(recentStatusFilter).description}
                </p>
              ) : null}

              {recentViewMode === 'messages' ? (
                <p className="mb-3 text-xs text-gray-500">
                  В режиме "По сообщениям" счётчики статусов показывают, у скольких сообщений такой статус встретился хотя бы в одном проекте.
                </p>
              ) : null}

              {recentPagination.totalItems > 0 ? (
                <div className="space-y-2">
                  {recentPagination.items.map((item, index) => {
                    if (recentViewMode === 'messages') {
                      const badgeStatus = item.statusCounts?.sent
                        ? 'sent'
                        : item.statusCounts?.queued
                          ? 'queued'
                          : item.statusCounts?.matched_but_not_routed
                            ? 'matched_but_not_routed'
                            : item.statusCounts?.send_failed
                              ? 'send_failed'
                              : item.statusCounts?.filtered_project_check
                                ? 'filtered_project_check'
                                : 'unknown';

                      return (
                        <div key={item.groupingKey || index} className="rounded border border-gray-200 p-2">
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                msg={item.messageId || '—'} · chat={item.chatId || '—'}
                              </p>
                              <p className="text-xs text-gray-500">
                                event={item.eventTimeLabel} · дата сообщения: {item.sourceDateLabel}
                              </p>
                            </div>
                            <span className={`text-xs rounded px-2 py-0.5 ${getMatchStatusBadgeClass(badgeStatus)}`}>
                              Проверено в {formatK(item.projectsChecked)} проектах
                            </span>
                          </div>

                          <p className="mt-1 text-xs text-gray-600">
                            Статусы: {item.statusBreakdown.length > 0 ? item.statusBreakdown.join(' · ') : '—'}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Причины: {item.reasonBreakdown.length > 0 ? item.reasonBreakdown.join(' · ') : '—'}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Проекты: {item.projectNames.length > 0 ? item.projectNames.join(', ') : '—'}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Ключи: {item.keywords.length > 0 ? item.keywords.join(', ') : '—'}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Сообщение: {item.messagePreview || '—'}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Ссылка на сообщение:{' '}
                            {item.sourceMessageLink ? (
                              <a
                                href={item.sourceMessageLink}
                                target="_blank"
                                rel="noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                открыть
                              </a>
                            ) : (
                              '—'
                            )}
                          </p>
                          <p className="mt-1 text-xs text-gray-600">
                            Report-группы: {item.reportGroupIds.length > 0 ? item.reportGroupIds.join(', ') : '—'}
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div key={`${item.messageId || 'msg'}-${item.projectId || 'p'}-${index}`} className="rounded border border-gray-200 p-2">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {item.projectName || `project#${item.projectId || '—'}`}
                            </p>
                            <p className="text-xs text-gray-500">
                              event={item.eventTimeLabel} · msg={item.messageId || '—'} · chat={item.chatId || '—'}
                            </p>
                            <p className="text-xs text-gray-500">
                              дата сообщения: {item.sourceDateLabel}
                            </p>
                          </div>
                          <span
                            className={`text-xs rounded px-2 py-0.5 ${getMatchStatusBadgeClass(item.status)}`}
                          >
                            {item.statusLabel}
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">{item.statusDescription}</p>
                        <p className="mt-1 text-xs text-gray-600">
                          Ключи: {item.keywords.length > 0 ? item.keywords.join(', ') : '—'}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Сообщение: {item.messagePreview || '—'}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Ссылка на сообщение:{' '}
                          {item.sourceMessageLink ? (
                            <a
                              href={item.sourceMessageLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              открыть
                            </a>
                          ) : (
                            '—'
                          )}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          Куда: {item.reportGroupId || '—'} {item.routeSource ? `(${item.routeSource})` : ''}
                        </p>
                        {item.deliveryDateLabel ? (
                          <p className="mt-1 text-xs text-gray-600">время доставки: {item.deliveryDateLabel}</p>
                        ) : null}
                        {item.telegramReportMessageId ? (
                          <p className="mt-1 text-xs text-gray-600">
                            report message_id: {item.telegramReportMessageId}
                          </p>
                        ) : null}
                        {item.reportMessageLink ? (
                          <p className="mt-1 text-xs text-gray-600">
                            Ссылка на report-сообщение:{' '}
                            <a
                              href={item.reportMessageLink}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              открыть
                            </a>
                          </p>
                        ) : null}
                        {item.reason ? (
                          <p className="mt-1 text-xs text-gray-500">
                            Причина: {item.reasonLabel || item.reason}
                            {item.reasonLabel && item.reasonLabel !== item.reason ? (
                              <span className="ml-1 text-gray-400">({item.reason})</span>
                            ) : null}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-500">По текущему фильтру событий нет.</p>
              )}

              {recentPagination.totalItems > 0 ? (
                <div className="mt-3 flex items-center justify-between gap-2">
                  <p className="text-xs text-gray-500">
                    Страница {recentPagination.currentPage} из {recentPagination.totalPages}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={recentPagination.currentPage <= 1}
                      onClick={() => setRecentPage(Math.max(1, recentPagination.currentPage - 1))}
                    >
                      Назад
                    </button>
                    <button
                      type="button"
                      className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                      disabled={recentPagination.currentPage >= recentPagination.totalPages}
                      onClick={() => setRecentPage(recentPagination.currentPage + 1)}
                    >
                      Вперёд
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">System last hour</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {formatK(hourlySummary.lastProcessed)} / {formatK(hourlySummary.lastMatched)}
              </p>
              <p className="mt-1 text-xs text-gray-500">processed / matched</p>
              <p className="mt-2 text-xs text-gray-600">window: last {hourlySummary.windowHours}h, avg {formatK(hourlySummary.avgPerHour)}/h</p>
            </div>
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs uppercase tracking-wide text-gray-500">System last day</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {formatK(dailySummary.lastProcessed)} / {formatK(dailySummary.lastMatched)}
              </p>
              <p className="mt-1 text-xs text-gray-500">processed / matched</p>
              <p className="mt-2 text-xs text-gray-600">window: last {dailySummary.windowDays}d, avg {formatK(dailySummary.avgPerDay)}/d</p>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default MessageVolumeMonitorPanel;
